import { Controller } from '@hotwired/stimulus';

const CLOSE_DELAY_MS = 150;
const VIEWPORT_MARGIN = 16;
const GAP = 4;

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static values = {
    targetId: String,
    align: { type: String, default: 'center' },
    placement: { type: String, default: 'top' },
    role: { type: String, default: 'tooltip' },
    trapFocus: Boolean,
    stopPropagation: Boolean,
  };

  initialize() {
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.reposition = this.reposition.bind(this);
    this.handleTargetKeydown = this.handleTargetKeydown.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.open = false;
  }

  connect() {
    // The React version portals the popover into <body> so it escapes any
    // clipping or stacking context of the trigger's subtree.
    this.portal();

    this.target = document.getElementById(this.targetIdValue);
    if (!this.target) {
      // eslint-disable-next-line no-console
      console.warn(`Tooltip: no element with id "${this.targetIdValue}" found`);
      return;
    }

    this.target.addEventListener('mouseenter', this.show);
    this.target.addEventListener('mouseleave', this.hide);
    this.target.addEventListener('focus', this.show);
    this.target.addEventListener('blur', this.hide);
    this.target.addEventListener('keydown', this.handleTargetKeydown);

    this.applyTargetAria();
  }

  disconnect() {
    this.clearCloseTimer();
    this.close();

    if (!this.target) return;

    this.target.removeEventListener('mouseenter', this.show);
    this.target.removeEventListener('mouseleave', this.hide);
    this.target.removeEventListener('focus', this.show);
    this.target.removeEventListener('blur', this.hide);
    this.target.removeEventListener('keydown', this.handleTargetKeydown);

    // Another popover with our id still in the document means we were either
    // just moved (portal) or replaced by a re-render — in both cases the live
    // instance owns the trigger's aria attributes, so leave them alone.
    if (!document.getElementById(this.element.id)) this.clearTargetAria();

    this.target = null;
  }

  applyTargetAria() {
    if (this.roleValue === 'tooltip') {
      this.target.setAttribute('aria-describedby', this.element.id);
    } else {
      this.target.setAttribute('aria-haspopup', 'dialog');
      this.target.setAttribute('aria-controls', this.element.id);
      this.target.setAttribute('aria-expanded', 'false');
    }
  }

  clearTargetAria() {
    if (this.roleValue === 'tooltip') {
      this.target.removeAttribute('aria-describedby');
    } else {
      this.target.removeAttribute('aria-haspopup');
      this.target.removeAttribute('aria-controls');
      this.target.removeAttribute('aria-expanded');
    }
  }

  show() {
    this.clearCloseTimer();
    if (this.open) return;

    this.open = true;
    this.element.hidden = false;
    this.element.style.visibility = 'hidden';
    this.reposition();

    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    document.addEventListener('keydown', this.handleEscape);

    if (this.roleValue === 'dialog' && this.target) {
      this.target.setAttribute('aria-expanded', 'true');
    }
  }

  hide() {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY_MS);
  }

  focusIn() {
    if (this.trapFocusValue) this.show();
  }

  focusOut() {
    if (this.trapFocusValue) this.hide();
  }

  popoverKeydown(event) {
    if (this.trapFocusValue && event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      if (this.target) this.target.focus();
    }
  }

  popoverClick(event) {
    if (this.stopPropagationValue) event.stopPropagation();
  }

  close() {
    if (!this.open) return;

    this.open = false;
    this.element.hidden = true;
    this.element.style.visibility = '';
    this.element.style.top = '';
    this.element.style.left = '';

    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    document.removeEventListener('keydown', this.handleEscape);

    if (this.roleValue === 'dialog' && this.target) {
      this.target.setAttribute('aria-expanded', 'false');
    }
  }

  reposition() {
    if (!this.target) return;

    const triggerRect = this.target.getBoundingClientRect();
    const popoverRect = this.element.getBoundingClientRect();

    const idealLeft = this.alignValue === 'start'
      ? triggerRect.left
      : triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    const maxLeft = window.innerWidth - popoverRect.width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, maxLeft));

    const aboveTop = triggerRect.top - GAP - popoverRect.height;
    const belowTop = triggerRect.bottom + GAP;
    const fitsAbove = aboveTop >= VIEWPORT_MARGIN;
    const fitsBelow = belowTop + popoverRect.height <= window.innerHeight - VIEWPORT_MARGIN;
    const preferBelow = this.placementValue === 'bottom';
    const top = (preferBelow ? fitsBelow || !fitsAbove : !fitsAbove && fitsBelow) ? belowTop : aboveTop;

    this.element.style.top = `${top}px`;
    this.element.style.left = `${left}px`;
    this.element.style.visibility = 'visible';
  }

  handleTargetKeydown(event) {
    if (this.stopPropagationValue && (event.key === ' ' || event.key === 'Enter')) {
      event.stopPropagation();
    }

    if (this.trapFocusValue && event.key === 'Tab' && !event.shiftKey && this.open) {
      const firstFocusable = this.element.querySelector(FOCUSABLE_SELECTOR);
      if (firstFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  handleEscape(event) {
    if (event.key !== 'Escape') return;

    this.close();
    if (this.target) this.target.focus();
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  portal() {
    if (this.element.parentElement === document.body) return;

    // A re-render of the owning view (turbo_stream, turbo-frame) leaves the
    // previously portalled popover orphaned in <body> — it never leaves the
    // document, so its controller never disconnects. Drop it here.
    const stale = document.getElementById(this.element.id);
    if (stale && stale !== this.element) stale.remove();

    document.body.appendChild(this.element);
  }
}
